import { getLetters } from '@/pages/api/scribeAPIV2';
import { useIsAuthenticated } from '@heidi/integrations/letters/data';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import type { components } from '@heidi/schema/ml-scribe';
import { queryOptions, useQuery } from '@tanstack/react-query';

type LetterResponse = components['schemas']['LetterResponse'];

export type QueryLettersResponse = LetterResponse[];

/** @deprecated Use lettersQueryOptions(sessionId).queryKey instead */
export const GET_LETTERS = 'letters';

export const lettersQueryOptions = (sessionId: string) =>
  queryOptions({
    queryKey: ['letters', { sessionId }] as const,
    queryFn: async (): Promise<QueryLettersResponse> => {
      const { data } = await getLetters(sessionId);
      return data.letters ?? [];
    },
  });

type UseQueryLettersParams<TData> = {
  select?: (data: QueryLettersResponse) => TData;
};

export function useQueryLetters<TData = QueryLettersResponse>({
  select,
}: UseQueryLettersParams<TData> = {}) {
  const sessionId = useCurrentSessionId();
  const isAuthenticated = useIsAuthenticated();
  const options = lettersQueryOptions(sessionId ?? '');

  return useQuery({
    ...options,
    enabled: !!sessionId && isAuthenticated,
    select,
  });
}

export function useQueryLetterById<TData = LetterResponse | undefined>({
  letterId,
  select,
}: {
  letterId: string;
  select?: (data: LetterResponse | undefined) => TData;
}) {
  return useQueryLetters({
    select: (letters) => {
      // Step 1: Find the single letter from the full list
      const singleLetter = letters.find((letter) => letter.id === letterId);

      // Step 2: If the consumer provided their own select, apply it to the single letter
      if (select) {
        return select(singleLetter);
      }

      // Otherwise, return the single letter as-is (cast to TData to satisfy TS)
      return singleLetter as unknown as TData;
    },
  });
}
